import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CCButton } from "@pymblecitizencoders/cc-ui";
import { TypedLottieSpinner } from "pymble-ui/components";
import { useEventPermissions } from "../hooks/useEventPublish";
import { WorkflowsAdmin } from "./PublishChains";
import { RolesAdmin } from "./PublishRoles";
import "../components/eventManager/eventManager.css";

type TabKey = "workflows" | "roles";

const TABS: { key: TabKey; label: string }[] = [
	{ key: "workflows", label: "Workflows" },
	{ key: "roles", label: "Roles" },
];

/**
 * Approval administration: the workflows, and the people who hold their roles.
 *
 * One screen rather than two, because the two halves are one question asked from
 * either end — a workflow names roles, a role is only real when somebody holds
 * it, and the failure that stops events dead sits between them: a workflow with
 * a step whose role has no live holder. Separately, that was two screens to
 * cross-check and a button on each pointing at the other.
 *
 * Tabs rather than a stack: the roles half is eight cards with a search inside
 * each, and putting them under the workflows would bury whichever half somebody
 * came for.
 *
 * `event_publish_admin` gates the whole screen, once, here. Assigning who signs
 * an event off is a different authority from administering events, and the API
 * draws the same line — the holder and workflow endpoints refuse anyone without
 * it, so offering either tab more widely would only hand people controls that
 * cannot save.
 */
export const PublishAdmin: React.FC = () => {
	const navigate = useNavigate();
	const { permissions, permissionsLoading } = useEventPermissions();
	const canAdmin = permissions?.event_publish_admin === true;
	const [tab, setTab] = useState<TabKey>("workflows");

	if (permissionsLoading) {
		return (
			<div className="em-page" data-surface="page">
				<div className="em-container">
					<div className="em-panel" data-surface="card">
						<div className="em-approve-loading">
							<TypedLottieSpinner
								type="Searching"
								size={180}
								label="Loading..."
							/>
						</div>
					</div>
				</div>
			</div>
		);
	}

	/* Said before anything is drawn, rather than letting the page render and
	   every control fail at the API. */
	if (!canAdmin) {
		return (
			<div className="em-page" data-surface="page">
				<div className="em-container">
					<div className="em-panel" data-surface="card">
						<div className="em-picker-empty">
							You do not have permission to administer approvals.
						</div>
						<CCButton
							variant="outline"
							size="sm"
							className="em-mt-sm"
							onClick={() => navigate("/admin")}
						>
							Back to sign-off
						</CCButton>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="em-page" data-surface="page">
			<div className="em-container">
				<div className="em-panel" data-surface="card">
					<div className="em-req-head">
						<div>
							<h6 className="em-section-heading">Approval administration</h6>
							<p className="em-muted">
								{tab === "workflows"
									? "Which roles sign an event off, and in what order. A workflow is chosen by the event’s type when it is submitted."
									: "Who holds each role. A role with nobody in it blocks every event whose workflow includes it."}
							</p>
						</div>
						<CCButton
							variant="outline"
							size="sm"
							onClick={() => navigate("/admin")}
						>
							Back to sign-off
						</CCButton>
					</div>

					<div className="em-tabbar" role="tablist">
						{TABS.map((t) => (
							<CCButton
								key={t.key}
								variant="underline"
								role="tab"
								selected={tab === t.key}
								aria-selected={tab === t.key}
								onClick={() => setTab(t.key)}
							>
								{t.label}
							</CCButton>
						))}
					</div>

					{/* Both stay mounted: each holds a draft — a half-edited workflow,
					    an open staff search — and switching tabs to check the other
					    half is not a decision to throw that away. */}
					<div hidden={tab !== "workflows"}>
						<WorkflowsAdmin />
					</div>
					<div hidden={tab !== "roles"}>
						<RolesAdmin />
					</div>
				</div>
			</div>
		</div>
	);
};
